import DOMPurify from "dompurify";

export interface RichTextElement {
  tag: string;
  props: {
    title?: string;
    href?: string;
    target?: "_blank";
    rel?: string;
    style?: { color?: string; backgroundColor?: string };
  };
  children: RichTextNode[];
}

export type RichTextNode = string | RichTextElement;

const allowedTags = [
  "p", "div", "span", "br", "strong", "b", "em", "i", "u", "s", "del",
  "ul", "ol", "li", "blockquote", "pre", "code", "a", "sub", "sup",
];
const allowedProtocols = new Set(["http:", "https:", "mailto:", "tel:"]);

function safeHref(value: string | null): string | undefined {
  if (!value?.trim()) return undefined;
  try {
    return allowedProtocols.has(new URL(value, window.location.href).protocol) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Preserve literal text/highlight colors, without layout CSS, URLs or CSS variables. */
function safeColors(element: HTMLElement): RichTextElement["props"]["style"] {
  const style: NonNullable<RichTextElement["props"]["style"]> = {};
  for (const property of ["color", "backgroundColor"] as const) {
    const value = element.style[property];
    if (/^(?:#[\da-f]{3,8}|[a-z]+|(?:rgb|rgba|hsl|hsla)\([\d\s.,%/+\-a-z]*\))$/i.test(value)) {
      style[property] = value;
    }
  }
  return Object.keys(style).length ? style : undefined;
}

/**
 * Shared HTML contract for banners and incident updates. Sanitize first, then
 * copy only text, supported tags and explicitly allowed props into a plain tree.
 * Consumers render this tree with Vue; never serialize it back into HTML.
 */
export function parseRichText(html: string): RichTextNode[] {
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: ["href", "title", "target", "style"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });

  function readChildren(parent: Node): RichTextNode[] {
    return Array.from(parent.childNodes).flatMap((node): RichTextNode[] => {
      if (node.nodeType === Node.TEXT_NODE) return [node.textContent ?? ""];
      if (!(node instanceof HTMLElement)) return [];
      const tag = node.localName;
      if (!allowedTags.includes(tag)) return [];
      const props: RichTextElement["props"] = {};
      const title = node.getAttribute("title");
      if (title) props.title = title;
      const style = safeColors(node);
      if (style) props.style = style;
      if (tag === "a") {
        const href = safeHref(node.getAttribute("href"));
        if (href) {
          props.href = href;
          if (node.getAttribute("target") === "_blank") {
            props.target = "_blank";
            props.rel = "noopener noreferrer";
          }
        }
      }
      return [{ tag, props, children: readChildren(node) }];
    });
  }

  return readChildren(fragment);
}
